/**
 * Pickup & Arrival Mode card (PROJECT_BRIEF §22.6).
 *
 * Shows on the parent home page whenever one or more children are
 * mid-commute home (left a school-flagged geofence with no matching
 * arrived-home event yet). For each active session we fetch an OSRM road
 * ETA from the child's most-recent pickup-event position to the family's
 * home geofence — same routing library that powers the panic track.
 *
 * The card disappears automatically once `pickup.arrived_home` fires (the
 * session is no longer in `/family/pickup-active`).
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueries } from '@tanstack/react-query';
import { Bus, Clock, Home, MapPin } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { analyticsApi, type PickupActive } from '@/api/analytics';
import { locationsApi } from '@/api/locations';
import { usersApi } from '@/api/users';
import { fetchRoute, formatDistance, formatDuration } from '@/lib/routing';
import { cn } from '@/lib/utils';
import { ChildAvatar } from '@/components/ChildAvatar';

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.max(0, Math.round(ms / 60_000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h} h ago` : `${h} h ${rem} min ago`;
}

function localTimeFromNow(deltaSeconds: number): string {
  const d = new Date(Date.now() + deltaSeconds * 1000);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

export function PickupCard() {
  const active = useQuery({
    queryKey: ['pickup', 'active'],
    queryFn: () => analyticsApi.pickupActive(4),
    refetchInterval: 30_000,
    retry: false,
  });

  const family = useQuery({
    queryKey: ['family-current'],
    queryFn: () => locationsApi.familyCurrent(),
    refetchInterval: 30_000,
    retry: false,
  });

  const children = useQuery({
    queryKey: ['children'],
    queryFn: () => usersApi.listChildren(),
    retry: false,
  });

  // Find the family's home geofence (icon='home'). All active sessions
  // route to the same home — multi-home families are out of scope for v1.
  const home = useMemo(
    () => (family.data?.geofences ?? []).find((g) => g.icon === 'home') ?? null,
    [family.data?.geofences],
  );

  const sessions = active.data ?? [];

  // One OSRM call per session, deduped by from/to in the queryKey. Slow
  // network or rate-limit → null, and we render the session without an ETA
  // rather than blocking the card.
  const routes = useQueries({
    queries: sessions.map((s) => ({
      queryKey: [
        'route',
        s.from_lat,
        s.from_lng,
        home?.center_lat ?? null,
        home?.center_lng ?? null,
      ],
      queryFn: () =>
        s.from_lat != null && s.from_lng != null && home
          ? fetchRoute([s.from_lng, s.from_lat], [home.center_lng, home.center_lat])
          : Promise.resolve(null),
      enabled: !!home && s.from_lat != null && s.from_lng != null,
      staleTime: 60_000,
      retry: false,
    })),
  });

  if (sessions.length === 0) return null;

  const childById = new Map((children.data ?? []).map((c) => [c.id, c]));

  return (
    <Card className="border-sky-200 bg-sky-50/50">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-sky-900">
          <Bus className="size-4" />
          On the way home
        </div>
        <div className="mt-3 grid gap-2.5">
          {sessions.map((s, i) => {
            const child = childById.get(s.child_id);
            const route = routes[i]?.data ?? null;
            const isLoading = routes[i]?.isPending ?? false;
            return <PickupRow key={s.child_id} session={s} child={child} route={route} routeLoading={isLoading} />;
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function PickupRow({
  session,
  child,
  route,
  routeLoading,
}: {
  session: PickupActive;
  child?: { name: string; color: string | null; avatar_url?: string | null };
  route: { distanceMeters: number; durationSeconds: number } | null;
  routeLoading: boolean;
}) {
  const name = child?.name ?? 'Child';
  const since = relativeTime(session.left_school_at);
  const eta = route ? localTimeFromNow(route.durationSeconds) : null;
  const dist = route ? formatDistance(route.distanceMeters) : null;
  const dur = route ? formatDuration(route.durationSeconds) : null;

  return (
    <div className="flex items-start gap-3 rounded-card bg-white/70 p-3">
      <ChildAvatar
        name={name}
        color={child?.color ?? '#0EA5E9'}
        avatarUrl={child?.avatar_url ?? null}
        size="size-11"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">
          {name} is on the way home
          {session.approaching && (
            <span className="ml-1.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
              Almost here
            </span>
          )}
          {session.running_late && (
            <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
              Running late
            </span>
          )}
        </p>
        <p className="mt-0.5 text-xs text-[color:var(--kz-color-ink-muted)]">
          <span className="inline-flex items-center gap-1">
            <MapPin className="size-3" />
            Left {session.school_name ?? 'school'} {since}
            {session.typical_minutes != null && (
              <span className={cn('ml-1', session.running_late ? 'text-amber-700' : '')}>
                · {session.elapsed_minutes} min in (usually ~{session.typical_minutes} min)
              </span>
            )}
          </span>
        </p>
        <p className={cn('mt-1 inline-flex items-center gap-1.5 text-xs font-medium', eta ? 'text-emerald-700' : 'text-[color:var(--kz-color-ink-muted)]')}>
          <Clock className="size-3.5" />
          {routeLoading && 'Calculating ETA…'}
          {eta && (
            <>
              ETA <span className="tabular-nums">{eta}</span>
              <span className="text-[color:var(--kz-color-ink-muted)]">
                · {dist} · {dur}
              </span>
            </>
          )}
          {!routeLoading && !eta && 'ETA unavailable — see live position'}
        </p>
      </div>
      <Button asChild variant="ghost" size="sm">
        <Link
          to={
            session.from_lat != null && session.from_lng != null
              ? `/map?focus=${session.child_id}&lat=${session.from_lat}&lng=${session.from_lng}&pin_label=${encodeURIComponent('Last seen')}`
              : `/map?focus=${session.child_id}`
          }
          aria-label={`Open ${name}'s location on the map`}
        >
          <Home className="size-3.5" />
          Track
        </Link>
      </Button>
    </div>
  );
}
